<script>
  import {
    flags,
    benefits,
    sections,
    ci,
    debug,
    saveKey,
    newestSection,
  } from "./store.js";
  import { SvelteToast } from "@zerodevx/svelte-toast";
  import { beforeUpdate, afterUpdate, onMount } from "svelte";
  import * as animateScroll from "svelte-scrollto";

  let style = "";
  if ($ci.page.body.font) style += `--font: ${$ci.page.body.font};`;
  if ($ci.page.body.font_weight)
    style += `--fontWeight ${$ci.page.body.font_weight};`;
  if ($ci.page.body.font_style)
    style += `--fontStyle ${$ci.page.body.font_style};`;
  if ($ci.page.body.size) style += `--size: ${$ci.page.body.size};`;
  if ($ci.page.body.max_width)
    style += `--max-width: ${$ci.page.body.max_width};`;
  if ($ci.page.body.line_height)
    style += `--lineHeight: ${$ci.page.body.line_height};`;
  if ($ci.page.header.height)
    style += `--padding-top: ${$ci.page.header.height};`;

  $: {
    if ($ci.page.color_background) {
      document.body.style.setProperty("--bg-color", $ci.page.color_background);
    }
    if ($ci.page.color_accent) {
      document.body.style.setProperty("--color-accent", $ci.page.color_accent);
    }
  }

  const toastOptions = {
    duration: 2000,
    reversed: true,
    intro: { x: 256 },
    theme: {
      "--toastProgressBackground":
        /*$ci.page.color_accent ?? */ "rgba(255,255,255,0.75)",
    },
  };

  import Header from "./Header.svelte";

  //import * as Widgets from "./widgets";
  import Heading from "./widgets/Heading.svelte";
  import Image from "./widgets/Image.svelte";
  import Text from "./widgets/Text.svelte";
  import Puzzle from "./widgets/Puzzle.svelte";
  import Benefit from "./widgets/Benefit.svelte";
  import Audio from "./widgets/Audio.svelte";
  import Video from "./widgets/Video.svelte";
  import Link from "./widgets/Link.svelte";
  import Container from "./widgets/Container.svelte";
  import Details from "./widgets/Details.svelte";
  import Button from "./widgets/Button.svelte";
  import Margin from "./widgets/Margin.svelte";
  import Embed from "./widgets/Embed.svelte";

  export const widgetDefs = {
    heading: Heading,
    image: Image,
    text: Text,
    puzzle: Puzzle,
    benefit: Benefit,
    audio: Audio,
    video: Video,
    link: Link,
    container: Container,
    details: Details,
    button: Button,
    margin: Margin,
    embed: Embed,
  };

  function shouldShowSection(identifier, section, flags, benefits) {
    if (!section.hasOwnProperty("show_on")) return true;

    let show = true;
    if (section.show_on.flag) show = show && flags.has(section.show_on.flag);
    if (section.show_on.benefit)
      show = show && benefits.has(section.show_on.benefit);

    if (show) {
      // first time this section is shown --> do scroll
      $newestSection = identifier;
    }

    return show;
  }

  $: {
    $newestSection, // reactive: on change in newest section variable, trigger timeout
      setTimeout(() => {
        animateScroll.scrollTo({
          element: `[data-id='${$newestSection}'`,
          offset: () => {
            return $ci.page.header.height
              ? parseInt($ci.page.header.height.replace(/[^0-9]/g, ""), 10) * -1
              : -100;
          },
        });
      }, 100);
  }
</script>

<svelte:head>
  <title>{$ci.strings.title ?? "Locked"}</title>
</svelte:head>

<SvelteToast options={toastOptions} />

<Header
  bgcolor={$ci.page.header.background_color}
  height={$ci.page.header.height}
  widgets={$ci.page.header.widgets}
  {style}
/>

<main {style}>
  {#if $debug}
    <section class="debug" style="text-align: left;" data-id="debug">
      <div style="display: inline-block;">
        State: {JSON.stringify([...$flags])}<br />Benefits: {JSON.stringify([
          ...$benefits,
        ])}
      </div>
      <button
        style="float: right; margin-right: 3rem"
        on:click={() => {
          localStorage.removeItem(`${saveKey}_benefits`);
          localStorage.removeItem(`${saveKey}_flags`);
          localStorage.removeItem(`${saveKey}_newest_section`);
          location.reload();
        }}>Reset globals</button
      >
    </section>
  {/if}

  {#each Object.entries($sections) as [identifier, section]}
    {#if shouldShowSection(identifier, section, $flags, $benefits)}
      <section
        class="locked-section"
        class:debug={$debug}
        data-id={identifier}
        style={"--margin-top: " + section["margin_top"] ?? ""}
      >
        {#each section["widgets"] as widget}
          <div
            class="locked-widget"
            class:debug={$debug}
            data-type={widget.type}
          >
            <svelte:component this={widgetDefs[widget.type]} {...widget} />
          </div>
        {/each}
      </section>
    {/if}
  {/each}
</main>
<div
  class="overscroll-container"
  style={`--overscroll: ${$ci.page.body.overscroll ?? 0}`}
/>

<style type="text/scss">
  :root {
    --toastContainerTop: auto;
    /*--toastContainerLeft: auto;*/
    --toastContainerBottom: 1.5rem;
    /*--toastContainerRight: 2rem;*/
    --toastWidth: 24rem;
  }

  :global(body) {
    background-color: var(--bg-color);
  }

  main {
    padding: 0.1em;
    padding-top: var(--padding-top);

    text-align: center;
    max-width: var(--max-width);
    margin: 0 auto;

    font-family: var(--font);
    font-size: var(--size);
    font-weight: var(--fontWeight);
    font-style: var(--fontStyle);
    line-height: var(--lineHeight);

    box-shadow: -1px -1px 19px -3px rgba(0, 0, 0, 0.75);
    background-color: white;
  }

  .overscroll-container {
    height: var(--overscroll, 0px);
    min-height: var(--overscroll, 0px);
    max-height: var(--overscroll, 0px);
  }

  .locked-section {
    padding: 1em;
    margin-top: var(--margin-top);
  }

  :global(.debug) {
    border: 1px dashed gray;
    position: relative;
  }
  :global(.debug:before) {
    content: attr(data-type);
    position: absolute;
    top: 0;
    right: 0;
    margin: 0 0.3em;
    font-size: 12px;
    background: rgba(255, 255, 255, 0.5);
  }
  :global(.locked-section.debug:before) {
    content: attr(data-id);
    left: 0;
  }

  @media (max-width: 960px) {
    main {
      max-width: 100%;
    }

    .locked-section {
      padding-left: 0.25em;
      padding-right: 0.25em;
    }
  }
</style>
